import React, { useEffect } from 'react';
import { updateSocialMeta } from '../utils/seo';
import { Card, CardContent } from '../components/ui/card';
import { Utensils } from 'lucide-react';

const Menu = () => {
  useEffect(() => {
    document.title = 'Menu - Pin High Golf & Lounge | Pizza & Food Menu in Bay Village';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'View our menu featuring pizza from Wolf & Co. and our shack menu. Hot dogs, bratwurst, popcorn, and more at Pin High Golf & Lounge in Bay Village.');
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/menu');
    updateSocialMeta({
      title: 'Menu - Pin High Golf & Lounge | Pizza & Food Menu in Bay Village',
      description: 'View our menu featuring pizza from Wolf & Co. and our shack menu. Hot dogs, bratwurst, popcorn, and more at Pin High Golf & Lounge in Bay Village.',
      path: '/menu',
    });
  }, []);

  const pizzas = [
    {
      name: 'Margherita',
      price: '$20',
      description: 'San Marzano tomato sauce, housemade mozzarella, parmesan, basil, EVOO.',
      vegetarian: true,
    },
    {
      name: 'Prosciutto',
      price: '$24',
      description: 'Roasted garlic, parmesan reggiano, fontina, prosciutto di parma, arugula, evoo, pistachio crumble.',
      vegetarian: false,
    },
    {
      name: "Nonna's Garden",
      price: '$21',
      description: 'Roasted garlic, shallots, blistered tomatoes, zucchini, squash, eggplant, basil, parsley, fontina.',
      vegetarian: true,
    },
    {
      name: 'Mushroom',
      price: '$21',
      description: 'White cream sauce, seasonal mushroom blend, rosemary.',
      vegetarian: true,
    },
    {
      name: 'Sausage',
      price: '$21',
      description: 'San Marzano tomato sauce, hot Italian sausage, mozzarella, provolone, shallot, fennel.',
      vegetarian: false,
    },
    {
      name: 'The Italiano',
      price: '$22',
      description: 'San Marzano tomato sauce, mozzarella, provolone, soppressata, peperoncini, parmesan.',
      vegetarian: false,
    },
    {
      name: 'Chicken Bianca',
      price: '$23',
      description: 'White cream sauce, chicken, fresh mozzarella, broccoli, sundried tomato, shallots, parmesan, arugula, chives.',
      vegetarian: false,
    },
    {
      name: 'Dill Pickle',
      price: '$23',
      description: 'Mozzarella, provolone, shallots, pickles, ranch, fresh dill.',
      vegetarian: true,
    },
    {
      name: 'Bourbon Street Pizza',
      price: '$25',
      description: 'San Marzano tomato sauce, mozzarella, provolone, shrimp, andouille sausage, Cajun seasoning, bell peppers, shallots, green onions, parsley.',
      vegetarian: false,
    },
    {
      name: 'Fall Harvest Pizza',
      price: '$23',
      description: 'Roasted garlic, mozzarella & provolone, kale, sweet potato, pancetta, craisins.',
      vegetarian: false,
    },
  ];

  const shackMenu = [
    {
      name: 'Hot Dog',
      price: '$4',
      description: '',
    },
    {
      name: 'Bratwurst',
      price: '$5',
      description: '',
    },
    {
      name: 'Free Popcorn',
      price: 'Free',
      description: '',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-green-600 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Utensils className="w-20 h-20 mx-auto mb-6 text-green-300" />
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            OUR MENU
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-green-50">
            Delicious food to fuel your game. Pizza from Wolf & Co. and classic halfway house favorites like hot dogs from our shack.
          </p>
        </div>
      </section>

      {/* Pizza Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              PIZZA FROM WOLF & CO.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pizzas.map((pizza, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-xl font-bold text-gray-900">
                      {pizza.name}
                      {pizza.vegetarian && (
                        <span className="ml-2 text-sm text-green-600 font-normal">(vg)</span>
                      )}
                    </h3>
                    <span className="text-2xl font-bold text-green-600">{pizza.price}</span>
                  </div>
                  <p className="text-gray-600 text-sm">{pizza.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Shack Menu Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              SHACK MENU
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {shackMenu.map((item, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.name}</h3>
                  <p className="text-2xl font-bold text-green-600">{item.price}</p>
                  {item.description && (
                    <p className="text-gray-600 text-sm mt-2">{item.description}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Menu;
